import { useState, type ReactNode } from 'react'
import { api, type StagePositions } from '../lib/api.ts'
import {
  checkHeadline,
  hasVerificationStage,
  type ErpCodeColumnCount,
  type ErpCodesCheckResult,
  type ErpStageRow,
} from '../lib/erpCodes.ts'
import {
  ERP_CODE_COLUMN_LABEL,
  ERP_CODE_VERDICT_LABEL,
  ErpCodeVerdict,
  STAGE_LABEL,
  stageOf,
} from '../lib/vocabulary.ts'
import { Button } from './Button.tsx'
import { TD, TH } from './DataTable.tsx'
import { useToast } from './Toast.tsx'

/**
 * What this account's ERP holds in each code column we decode (#672), on the
 * account's page below where its prescriptions come from.
 *
 * Every one of those tables was learnt on ArtPharma's Formula Certa. The
 * check counts the account's own values and marks the ones no table knows,
 * beside the installation's list of bench stages, which is what a second
 * pharmacy has to confirm before it is verified. Read-only and counts only,
 * so it runs with the integration off too.
 */
export function AccountErpCodes({ accountId }: { accountId: string }) {
  const [result, setResult] = useState<ErpCodesCheckResult | undefined>()
  const [checking, setChecking] = useState(false)
  const toast = useToast()

  async function check() {
    setChecking(true)
    try {
      setResult(await api.checkAccountErpCodes(accountId))
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao verificar', { tone: 'error' })
    } finally {
      setChecking(false)
    }
  }

  return (
    <section className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-4 border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex max-w-[70ch] flex-col gap-1">
          <h2 className="text-tPSemi text-text-strong">Códigos do ERP</h2>
          <p className="text-tSmall text-text-weak">
            As tabelas que traduzem os códigos do Formula Certa (forma, via, cápsula, tipo de linha,
            associação, conselho e lista de controlados) foram aprendidas na ArtPharma. A
            verificação conta os valores de cada coluna no ERP desta conta e destaca os que elas não
            conhecem. Só leitura e só contagens: nenhum dado de paciente sai do ERP.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          tone="neutral"
          size="small"
          disabled={checking}
          onClick={() => void check()}
        >
          {checking ? 'Verificando…' : 'Verificar códigos'}
        </Button>
      </div>
      {result !== undefined && <ErpCodesResult result={result} />}
    </section>
  )
}

/** The check's answer, drawn from data alone. */
export function ErpCodesResult({ result }: { result: ErpCodesCheckResult }) {
  if (!result.ok) {
    return <p className="text-tP text-text-error">O ERP não respondeu: {result.error}</p>
  }
  const headline = checkHeadline(result)
  return (
    <div className="flex flex-col gap-5">
      <p className={`text-tP ${headline.warn ? 'text-text-warning' : 'text-text-weak'}`}>
        {`Requisições criadas nos últimos ${result.windowDays} dias, contadas em ${seconds(result.ms)}. `}
        {headline.text}
      </p>
      <ErpStageList
        stages={result.stages}
        error={result.stagesError}
        positions={result.stagePositions}
      />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {result.columns.map((column) => (
          <ErpCodeTable key={column.column} column={column} />
        ))}
      </div>
    </div>
  )
}

/** One column: each value, how often, what we read it as, and whether a table knows it. */
export function ErpCodeTable({ column }: { column: ErpCodeColumnCount }) {
  const label = ERP_CODE_COLUMN_LABEL[column.column]
  return (
    <div className="flex flex-col gap-2">
      <Heading title={label.title} source={column.source} scope={label.scope} />
      {column.error !== null ? (
        <p className="text-tSmall text-text-error">Não foi possível contar: {column.error}</p>
      ) : column.codes.length === 0 ? (
        <p className="text-tSmall text-text-weak">Nenhum valor no período.</p>
      ) : (
        <Table headings={['Valor', 'Quantidade', 'Lemos como', 'Situação']}>
          {column.codes.map((found) => {
            const unknown = found.verdict === ErpCodeVerdict.Unknown
            return (
              <tr key={found.code} className={unknown ? 'bg-fill-warning-weak' : undefined}>
                <td className={`${TD} font-mono`}>
                  {found.code === '' ? <span className="text-text-weak">(vazio)</span> : found.code}
                </td>
                <td className={`${TD} tabular-nums`}>{found.count.toLocaleString('pt-BR')}</td>
                <td className={TD}>{found.reading ?? '—'}</td>
                <td className={`${TD} ${unknown ? 'text-text-warning' : 'text-text-weak'}`}>
                  {unknown ? (
                    <strong>{ERP_CODE_VERDICT_LABEL[found.verdict]}</strong>
                  ) : (
                    ERP_CODE_VERDICT_LABEL[found.verdict]
                  )}
                </td>
              </tr>
            )
          })}
        </Table>
      )}
    </div>
  )
}

/** `FC12540`, and where our queue puts each of its stages on the account's positions (#673). */
export function ErpStageList({
  stages,
  error,
  positions,
}: {
  stages: ErpStageRow[] | null
  error: string | null
  positions: StagePositions
}) {
  return (
    <div className="flex flex-col gap-2">
      <Heading
        title="Etapas da bancada"
        source="FC12540"
        scope="A fila lê a fase de cada requisição pela posição da etapa em que ela está, nas posições desta conta (Etapas da bancada no ERP)."
      />
      {error !== null ? (
        <p className="text-tSmall text-text-error">Não foi possível ler as etapas: {error}</p>
      ) : stages === null || stages.length === 0 ? (
        <p className="text-tSmall text-text-weak">Nenhuma etapa cadastrada.</p>
      ) : (
        <>
          {!hasVerificationStage(stages, positions) && (
            <p className="text-tSmall text-text-warning">
              {`Nenhuma etapa está entre as posições que esta conta lê como conferência (de ${positions.conferencia} até antes de ${positions.producao}): nesta instalação, nenhuma requisição chegaria à conferência.`}
            </p>
          )}
          <Table headings={['Código', 'Posição', 'Descrição', 'Na nossa fila']}>
            {stages.map((stage) => (
              <tr key={`${stage.code}-${stage.position}`}>
                <td className={`${TD} font-mono`}>{stage.code}</td>
                <td className={`${TD} tabular-nums`}>{stage.position}</td>
                <td className={TD}>{stage.name}</td>
                <td className={TD}>{STAGE_LABEL[stageOf(stage.position, positions)]}</td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </div>
  )
}

function Heading({
  title,
  source,
  scope,
}: {
  title: string
  source: string
  scope?: string | undefined
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <h3 className="text-tPSemi text-text-strong">
        {title} <span className="text-tSmall text-text-weak font-mono">{source}</span>
      </h3>
      {scope !== undefined && <p className="text-tSmall text-text-weak">{scope}</p>}
    </div>
  )
}

function Table({ headings, children }: { headings: readonly string[]; children: ReactNode }) {
  return (
    <div className="rounded-base border-stroke-weak w-full overflow-x-auto border">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {headings.map((heading) => (
              <th key={heading} scope="col" className={TH}>
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

function seconds(ms: number): string {
  return `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`
}
